import { HttpErrorResponse, HttpInterceptorFn, HttpClient, HttpBackend } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { from, of, throwError } from 'rxjs';
import {
  retryWhen,
  concatMap,
  delay,
  catchError,
  switchMap,
  map,
} from 'rxjs/operators';
import { SpinnerService } from '../service/spinner.service';
import { ToastService } from '../service/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export const retryCount = 1;
export const retryWaitMilliSeconds = 1000;

const isRefreshOrLoginEndpoint = (url: string) =>
  url.includes('/refresh-token') || url.includes('/login');

const extractErrorMessage$ = (err: any) => {
  if (err?.error instanceof Blob) {
    return from(err.error.text()).pipe(
      map((text: string) => {
        try {
          const j = JSON.parse(text);
          return j.message ?? j.error ?? text;
        } catch {
          return text;
        }
      })
    );
  }
  
  const errorMsg = err?.error?.message ?? err?.error?.error ?? err?.error ?? err.message;
  
  if (typeof errorMsg === 'object' && errorMsg !== null) {
    if ('error' in errorMsg) {
      return of(errorMsg.error);
    }
    if ('message' in errorMsg) {
      return of(errorMsg.message);
    }
    return of(JSON.stringify(errorMsg));
  }
  
  if (typeof errorMsg === 'string') return of(errorMsg);
  return of(err?.message ?? 'Unknown error');
};

let isRefreshing = false;
let refreshTokenPromise: Promise<string> | null = null;

export const ErrorInterceptor: HttpInterceptorFn = (request, next) => {
  const spinner = inject(SpinnerService);
  const translate = inject(TranslateService);
  const cookieService = inject(CookieService);
  const router = inject(Router);
  const httpBackend = inject(HttpBackend);
  
  // CRITICAL: Injection paresseuse de ToastService via Injector
  const injector = inject(Injector);
  let toastService: ToastService | null = null;
  const getToastService = () => {
    if (!toastService) {
      toastService = injector.get(ToastService);
    }
    return toastService;
  };

  // Helper to get current JWT
  const getCurrentJwt = () => {
    return cookieService.get(environment.jwtName) || '';
  };

  // Helper to perform refresh WITHOUT going through interceptors
  const performRefresh = (): Promise<string> => {
    if (isRefreshing && refreshTokenPromise) {
      return refreshTokenPromise;
    }

    isRefreshing = true;
    
    // Create a new HttpClient instance that bypasses interceptors
    const httpWithoutInterceptors = new HttpClient(httpBackend);

    refreshTokenPromise = new Promise<string>((resolve, reject) => {
      httpWithoutInterceptors
        .post<{ token: string }>(`/api/SignIn/refresh-token`, {}, {
          headers: {
            Authorization: `Bearer ${getCurrentJwt()}`,
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .subscribe({
          next: (response) => {
            const newToken = response.token;
            
            // Update cookie
            cookieService.set(environment.jwtName, newToken, {
              expires: 1,
              path: '/',
              domain: null,
              secure: true,
              sameSite: 'Strict'
            });
            
            isRefreshing = false;
            refreshTokenPromise = null;
            resolve(newToken);
          },
          error: (err) => {
            isRefreshing = false;
            refreshTokenPromise = null;
            reject(err);
          }
        });
    });

    return refreshTokenPromise;
  };

  // Helper to logout
  const performLogout = () => {
    cookieService.delete(environment.jwtName, '/');
    cookieService.delete('refreshToken', '/');
    router.navigate(["login"]);
  };

  return next(request).pipe(
    // Retry simple sur timeout ou 400 sans corps
    retryWhen(errors =>
      errors.pipe(
        concatMap((error, count) =>
          count < retryCount &&
            (error.status === 0 || (error.status === 400 && !error.error))
            ? of(error)
            : throwError(() => error)
        ),
        delay(retryWaitMilliSeconds)
      )
    ),

    // Handle 401/403 with token refresh
    catchError(err => {
      if (
        (err.status === 401 || err.status === 403) &&
        !isRefreshOrLoginEndpoint(request.url)
      ) {
        return from(performRefresh()).pipe(
          catchError(refreshErr => {
            performLogout();
            return throwError(() => refreshErr);
          }),
          switchMap(newToken => {
            const cloned = request.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` }
            });
            return next(cloned);
          })
        );
      }
      return throwError(() => err);
    }),

    // Handle other errors
    catchError(err => {
      return extractErrorMessage$(err).pipe(
        switchMap((msg: string) => {
          spinner.hide();

          // Utiliser le ToastService de manière paresseuse
          const toast = getToastService();
          
          if (err.status === 400) {
            toast.showError(translate.instant('Error'), msg);
          } else if (err.status === 504) {
            toast.showError(translate.instant('Timeout Error'), '');
          } else if (err.status >= 500) {
            toast.showError(translate.instant('Internal Error'), '');
          } else if (err.status !== 303 && !environment.production) {
            toast.showError('Erreur', msg || JSON.stringify(err));
          }

          const enriched = new HttpErrorResponse({
            error: msg,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined
          });
          return throwError(() => enriched);
        })
      );
    })
  );
};
